import {isEqual, round, uniqWith} from 'lodash';
import React from 'react';

import {useUnitSystem} from 'src/contexts/UnitSystemContext';
import useForecastTimes from 'src/hooks/useForecastTimes';
import {Site} from 'src/lib/api/project';
import {
  DashboardSection,
  ForecastTime,
  Source,
  UnitSystem,
  WeatherColumns,
} from 'src/lib/api/series';
import {addDays} from 'src/lib/dates';
import {ConfigBySourceId} from 'src/lib/sourceConfigs';

import TimeSeriesProvider from './TimeSeriesProvider';
import {useTranslations} from './TranslationsContext';
import {VizConfigCreatorItem, makeConfigItems, makeRequestFromVizConfig} from './Viz/config';

type Props = {
  site: Site;
  horizonDays: number;
  section: DashboardSection;
};

// cumulative precipation over horizonDay by source
type SourcedPrecipitation = {
  source: Source;
  unitSystem?: UnitSystem;
  precipitation: number;
};

export default function ForecastPrecipitationTextSummary({site, horizonDays, section}: Props) {
  const {t} = useTranslations();
  const unitSystem = useUnitSystem();
  const [initTimeStartDate, initTimeEndDate] = React.useMemo(() => {
    return [addDays(new Date(), -1), new Date()];
  }, []);

  const sources = uniqWith(Object.values(site.forecastTimeSeries).flat(), isEqual)
    .filter((q) => q.columns?.includes(WeatherColumns.PRECIPITATION))
    .map((q) => q.source);

  const forecastTimesRequest = useForecastTimes(
    site.id,
    sources,
    initTimeStartDate,
    initTimeEndDate,
    false
  );

  const dateRange = React.useMemo(
    () => [new Date(), addDays(new Date(), horizonDays)],
    [horizonDays]
  ) as [Date, Date];

  const forecastTimesBySource = forecastTimesRequest.data;
  if (!forecastTimesBySource) {
    return <></>;
  }

  const config = makeConfig(sources);

  const latestForecastTimes: {[key in Source]?: ForecastTime[]} = {};

  for (const source of sources) {
    latestForecastTimes[source] = [forecastTimesBySource[source]![0]];
  }

  return (
    <div>
      <TimeSeriesProvider
        site={site}
        section={section}
        forecastTimesBySource={latestForecastTimes}
        dateRange={dateRange}
        {...makeRequestFromVizConfig(config)}
      >
        {(vizProps) => {
          let minForecast: JSX.Element | null = (
            <span className="inline-block w-24 h-6 bg-blue-100 rounded-sm -mb-2"></span>
          );

          let maxForecast: JSX.Element | null = (
            <span className="inline-block w-24 h-6 bg-blue-100 rounded-sm -mb-2"></span>
          );

          if (!vizProps.isLoading) {
            if (vizProps.forecastTimeSeriesList.length === 0) {
              // rare case where maybe a forecast is being backfilled and its issue time is valid but the api doesn't return a forecast
              return <div>{t('forecastInProgress')}</div>;
            }

            const sourcedForecasts: SourcedPrecipitation[] = vizProps.forecastQueries.flatMap(
              (query, i) => {
                const precipitation =
                  vizProps.forecastTimeSeriesList[i]?.forecasts[0]?.data.precipitation[
                    horizonDays - 1
                  ];
                return precipitation == null
                  ? []
                  : [{source: query.source, unitSystem, precipitation}];
              }
            );

            [minForecast, maxForecast] = getMinMaxPrecipitationDescription(sourcedForecasts);

            if (!minForecast && !maxForecast) {
              return <p>{t('noTrendAvailable')}</p>;
            }
          }

          return (
            <div>
              <p>
                ➙ {t('totalAccumulatedPrecipitation')} {minForecast} {t('and')} {maxForecast}.
              </p>
            </div>
          );
        }}
      </TimeSeriesProvider>
    </div>
  );
}

type MinMaxPrecipitation = {
  min: SourcedPrecipitation;
  max: SourcedPrecipitation;
};

function getMinMaxPrecipitation(sourcedForecasts: SourcedPrecipitation[]): MinMaxPrecipitation {
  return sourcedForecasts.reduce<MinMaxPrecipitation>(
    (acc, series) => {
      if (series.precipitation > acc.max.precipitation) {
        acc.max = series;
      }

      if (series.precipitation < acc.min.precipitation) {
        acc.min = series;
      }

      return acc;
    },
    {
      min: {source: Source.GFS, unitSystem: UnitSystem.US, precipitation: Infinity},
      max: {source: Source.GFS, unitSystem: UnitSystem.US, precipitation: 0},
    }
  );
}

function getMinMaxPrecipitationDescription(
  sourcedForecasts: SourcedPrecipitation[]
): JSX.Element[] | null[] {
  const minMax: MinMaxPrecipitation = getMinMaxPrecipitation(sourcedForecasts);

  if (minMax.min.precipitation === Infinity && minMax.max.precipitation === 0) {
    return [null, null];
  }

  const unitSystem = sourcedForecasts[0].unitSystem;
  const unit = unitSystem === UnitSystem.Metric ? 'mm' : 'in';

  return [
    <>
      <span>
        {round(minMax.min.precipitation, 2).toLocaleString()} {unit}
      </span>{' '}
      ({ConfigBySourceId[minMax.min.source].display})
    </>,
    <>
      <span>
        {round(minMax.max.precipitation, 2).toLocaleString()} {unit}
      </span>{' '}
      ({ConfigBySourceId[minMax.max.source].display})
    </>,
  ];
}

function makeConfig(sources: string[]) {
  return {
    forecasts: makeConfigItems(
      'forecasts',
      sources.map((source) => ({
        displayName: '',
        source: source,
        columns: [WeatherColumns.PRECIPITATION],
        getStyle: () => ({}),
        legendIcon: 'line',
      })) as VizConfigCreatorItem[],
      {
        timeAggregation: '1D',
        rateVolumeMode: 'cumulative-volume',
      }
    ),
    observations: makeConfigItems('observations', [], {}),
  };
}
